import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarClock, Plus, Trash2, Copy, Check, Loader2, AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

// Day keys use the JS getDay() convention (0 = Sunday … 6 = Saturday) so the
// venue tablet can pass its own local day straight through to the picker.
const DAYS = [
  { key: '1', label: 'Mon', full: 'Monday' },
  { key: '2', label: 'Tue', full: 'Tuesday' },
  { key: '3', label: 'Wed', full: 'Wednesday' },
  { key: '4', label: 'Thu', full: 'Thursday' },
  { key: '5', label: 'Fri', full: 'Friday' },
  { key: '6', label: 'Sat', full: 'Saturday' },
  { key: '0', label: 'Sun', full: 'Sunday' },
];

const ENERGY_ACCENT = {
  chill: '#7DD3FC', upbeat: '#39FF14', groovy: '#FFB300', dance: '#FF007F',
};

const emptySchedule = () => ({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] });

// Stable per-row ids so React keys survive reorder/removal (never sent to the
// backend — stripped before save; the validator ignores extras regardless).
let _rid = 0;
const uid = () => `r${Date.now().toString(36)}${(_rid += 1).toString(36)}`;
const withIds = (rows) => (rows || []).map((r) => ({ ...r, _id: r._id || uid() }));
const cloneFresh = (rows) => (rows || []).map((r) => ({ ...r, _id: uid() }));
const stripIds = (sched) => {
  const out = {};
  for (let i = 0; i < 7; i += 1) {
    out[i] = ((sched && (sched[i] || sched[String(i)])) || []).map(({ _id, ...rest }) => rest);
  }
  return out;
};

const toMinutes = (s) => {
  const [h, m] = String(s || '').split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// First-match-wins overlap detection within a single day (for an inline hint).
const findOverlaps = (rows) => {
  const spans = rows
    .map((r, i) => ({ i, s: toMinutes(r.from), e: toMinutes(r.to) }))
    .filter((x) => x.s != null && x.e != null && x.s !== x.e);
  const bad = new Set();
  for (let a = 0; a < spans.length; a += 1) {
    for (let b = a + 1; b < spans.length; b += 1) {
      const A = spans[a]; const B = spans[b];
      const covers = (sp, t) => (sp.s < sp.e ? (t >= sp.s && t < sp.e) : (t >= sp.s || t < sp.e));
      if (covers(A, B.s) || covers(B, A.s)) { bad.add(A.i); bad.add(B.i); }
    }
  }
  return bad;
};

/**
 * Shared weekly AI day-plan editor. Used by the venue operator (their own
 * Settings) and the super-admin (per-venue). Pure presentational + local
 * draft state; the parent owns the API call via `onSave`.
 */
export const AiScheduleEditor = ({
  enabled: enabledProp,
  schedule: scheduleProp,
  genres = [],
  energies = [],
  onSave,
  saving = false,
}) => {
  const [enabled, setEnabled] = useState(!!enabledProp);
  const [draft, setDraft] = useState(() => {
    const base = emptySchedule();
    const src = scheduleProp || {};
    for (let i = 0; i < 7; i += 1) base[i] = withIds(src[i] || src[String(i)] || []);
    return base;
  });
  const [activeDay, setActiveDay] = useState('1');
  const [copyFromOpen, setCopyFromOpen] = useState(false);

  const g0 = genres[0] || 'house';
  const e0 = energies[0] || 'chill';

  const baseline = useMemo(() => JSON.stringify({ enabled: !!enabledProp, schedule: stripIds(scheduleProp || {}) }),
    [enabledProp, scheduleProp]);
  const dirty = JSON.stringify({ enabled, schedule: stripIds(draft) }) !== baseline;

  const rows = useMemo(() => draft[activeDay] || [], [draft, activeDay]);
  const overlaps = useMemo(() => findOverlaps(rows), [rows]);

  const mutate = (fn) => setDraft((prev) => {
    const next = { ...prev };
    fn(next);
    return next;
  });

  const addRow = () => mutate((n) => {
    n[activeDay] = [...(n[activeDay] || []), { _id: uid(), from: '18:00', to: '23:00', genre: g0, energy: e0 }];
  });
  const updateRow = (idx, patch) => mutate((n) => {
    n[activeDay] = (n[activeDay] || []).map((r, i) => (i === idx ? { ...r, ...patch } : r));
  });
  const removeRow = (idx) => mutate((n) => {
    n[activeDay] = (n[activeDay] || []).filter((_, i) => i !== idx);
  });
  const copyToAll = () => {
    const src = draft[activeDay] || [];
    mutate((n) => { for (let i = 0; i < 7; i += 1) n[String(i)] = cloneFresh(src); });
    toast.success('Copied this day to every day of the week');
  };
  const copyFrom = (srcKey) => {
    const src = draft[srcKey] || [];
    mutate((n) => { n[activeDay] = cloneFresh(src); });
    setCopyFromOpen(false);
    const d = DAYS.find((x) => x.key === srcKey);
    toast.success(`Copied ${d?.full || srcKey} into ${DAYS.find((x) => x.key === activeDay)?.full}`);
  };

  const save = async () => {
    // Light client-side guard; backend validates authoritatively.
    for (const dk of Object.keys(draft)) {
      for (const r of draft[dk]) {
        if (toMinutes(r.from) == null || toMinutes(r.to) == null) {
          toast.error('Every time block needs a valid start and end time');
          return;
        }
        if (r.from === r.to) {
          toast.error('A block\u2019s start and end time can\u2019t be the same');
          return;
        }
      }
    }
    try {
      const res = await onSave(enabled, stripIds(draft));
      if (res && res.schedule) {
        const base = emptySchedule();
        for (let i = 0; i < 7; i += 1) base[i] = withIds(res.schedule[i] || res.schedule[String(i)] || []);
        setDraft(base);
        setEnabled(!!res.enabled);
      }
      toast.success('Schedule saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save schedule');
    }
  };

  const dayCount = (key) => (draft[key] || []).length;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-6" data-testid="ai-schedule-editor">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#00FFFF]/15 ring-1 ring-[#00FFFF]/40 flex items-center justify-center flex-shrink-0">
          <CalendarClock className="w-5 h-5 text-[#00FFFF]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold tracking-tight text-white">Scheduled Day-Plan</h3>
          <p className="text-sm text-gray-400 mt-0.5">
            Program time blocks per day (from → to, genre, energy). When a block is live it overrides
            Auto/Manual; any gap falls back to the base Auto vibe.
          </p>
        </div>
      </div>

      {/* Master enable toggle */}
      <div className="flex items-center gap-3 p-3 rounded-2xl bg-black/30 border border-white/5 mb-5">
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ai-schedule-enabled" />
        <div className="text-xs">
          <p className="text-white font-semibold">{enabled ? 'Schedule is ON' : 'Schedule is OFF'}</p>
          <p className="text-gray-500">
            {enabled
              ? 'The booth follows this timetable when a block matches the current time.'
              : 'Turn on to let the timetable drive the booth automatically.'}
          </p>
        </div>
      </div>

      {/* Day tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="ai-schedule-day-tabs">
        {DAYS.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => { setActiveDay(d.key); setCopyFromOpen(false); }}
            data-testid={`ai-schedule-day-${d.key}`}
            className={`relative px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              activeDay === d.key ? 'bg-[#00FFFF] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {d.label}
            {dayCount(d.key) > 0 && (
              <span className={`ml-1.5 text-[10px] ${activeDay === d.key ? 'text-black/60' : 'text-[#00FFFF]'}`}>
                {dayCount(d.key)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Copy toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          type="button"
          onClick={copyToAll}
          variant="ghost"
          className="h-8 px-3 rounded-full text-[11px] font-bold tracking-wider border border-white/10 text-gray-200 hover:bg-white/10"
          data-testid="ai-schedule-copy-to-all"
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Apply this day to all
        </Button>
        <div className="relative">
          <Button
            type="button"
            onClick={() => setCopyFromOpen((o) => !o)}
            variant="ghost"
            className="h-8 px-3 rounded-full text-[11px] font-bold tracking-wider border border-white/10 text-gray-200 hover:bg-white/10"
            data-testid="ai-schedule-copy-from"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy from…
          </Button>
          {copyFromOpen && (
            <div className="absolute z-20 mt-1 w-40 rounded-xl bg-[#0a0a0a] border border-white/10 shadow-xl p-1" data-testid="ai-schedule-copy-from-menu">
              {DAYS.filter((d) => d.key !== activeDay).map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => copyFrom(d.key)}
                  className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-gray-200 hover:bg-white/10 flex items-center justify-between"
                  data-testid={`ai-schedule-copy-from-${d.key}`}
                >
                  {d.full}
                  <span className="text-gray-500">{dayCount(d.key)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rows for the active day */}
      <div className="space-y-2" data-testid="ai-schedule-rows">
        {rows.length === 0 ? (
          <p className="text-[12px] text-gray-500 py-3 text-center border border-dashed border-white/10 rounded-2xl" data-testid="ai-schedule-empty">
            No blocks for {DAYS.find((d) => d.key === activeDay)?.full}. The booth plays the base Auto vibe all day.
          </p>
        ) : (
          rows.map((r, idx) => {
            const accent = ENERGY_ACCENT[r.energy] || '#FF007F';
            return (
              <div
                key={r._id}
                className={`flex flex-wrap items-center gap-2 p-2.5 rounded-2xl bg-black/30 border ${overlaps.has(idx) ? 'border-[#FFB300]/50' : 'border-white/5'}`}
                data-testid={`ai-schedule-row-${activeDay}-${idx}`}
              >
                <input
                  type="time"
                  value={r.from}
                  onChange={(e) => updateRow(idx, { from: e.target.value })}
                  style={{ colorScheme: 'dark' }}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#00FFFF]/50"
                  data-testid={`ai-schedule-from-${activeDay}-${idx}`}
                />
                <span className="text-gray-500 text-xs">to</span>
                <input
                  type="time"
                  value={r.to}
                  onChange={(e) => updateRow(idx, { to: e.target.value })}
                  style={{ colorScheme: 'dark' }}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#00FFFF]/50"
                  data-testid={`ai-schedule-to-${activeDay}-${idx}`}
                />
                <select
                  value={r.genre}
                  onChange={(e) => updateRow(idx, { genre: e.target.value })}
                  style={{ colorScheme: 'dark' }}
                  className="flex-1 min-w-[110px] bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#39FF14]/50 capitalize"
                  data-testid={`ai-schedule-genre-${activeDay}-${idx}`}
                >
                  {genres.map((g) => (
                    <option key={g} value={g}>{g.replace(/-/g, ' ')}</option>
                  ))}
                </select>
                <select
                  value={r.energy}
                  onChange={(e) => updateRow(idx, { energy: e.target.value })}
                  style={{ colorScheme: 'dark', color: accent }}
                  className="min-w-[90px] bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs font-semibold uppercase focus:outline-none focus:border-white/30"
                  data-testid={`ai-schedule-energy-${activeDay}-${idx}`}
                >
                  {energies.map((e) => (
                    <option key={e} value={e} style={{ color: '#fff' }}>{e}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="text-gray-500 hover:text-[#FF3B3B] transition-colors p-1.5"
                  aria-label="Remove block"
                  data-testid={`ai-schedule-remove-${activeDay}-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {overlaps.size > 0 && (
        <p className="text-[11px] text-[#FFB300] mt-2 flex items-center gap-1.5" data-testid="ai-schedule-overlap-hint">
          <AlertTriangle className="w-3.5 h-3.5" /> Some blocks overlap — the earliest matching block wins.
        </p>
      )}

      <div className="flex items-center gap-3 mt-4">
        <Button
          type="button"
          onClick={addRow}
          variant="ghost"
          className="h-9 px-4 rounded-full text-xs font-bold border border-[#00FFFF]/30 text-[#00FFFF] hover:bg-[#00FFFF]/10"
          data-testid="ai-schedule-add-row"
        >
          <Plus className="w-4 h-4 mr-1" /> Add time block
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="ai-schedule-save"
          className={`h-9 px-6 rounded-full font-bold text-xs ml-auto ${dirty ? 'bg-[#39FF14] text-black hover:bg-[#2fd60f]' : 'bg-white/10 text-gray-400'}`}
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : dirty ? 'Save schedule' : <><Check className="w-4 h-4 mr-2" /> Saved</>}
        </Button>
      </div>
    </div>
  );
};

export default AiScheduleEditor;
